import React, { useEffect, useState } from 'react';
import { getAllStations, getAllStationTypes, Station, StationType, isStationBooked, subscribeToBookings, createBookingDates, getConfig, createBooking, isUserAdmin, getAllBookings } from '../Firebase/firebaseApi.ts';
import { logInfo } from '../Firebase/auditLog.ts';
import { trackEvent } from '../Analytics/posthog.ts';
import { translateApiError } from '../utils/errorTranslations.ts';
import ChooseStationType from '../ChooseStationType/ChooseStationType.tsx';
import DateTimePicker from './DateTimePicker.tsx';
import StationList from './StationList.tsx';
import BookingDialog from './BookingDialog.tsx';
import { buildSeriesRanges, getConflictDates } from './seriesAvailability.ts';
import { getAuth } from 'firebase/auth';
import { Container, Typography, Button, Box, ToggleButton, ToggleButtonGroup, Paper } from '@mui/material';
import BackgroundWrapper from '../components/BackgroundWrapper.tsx';
import './Booking.css';

// Minimum date allowed: January 13, 2026
const MIN_ALLOWED_DATE = new Date(2026, 0, 13); // month is 0-indexed (0 = January)
MIN_ALLOWED_DATE.setHours(0, 0, 0, 0);

// Helper functions
// Function to get available slot types from payLinks
function getAvailableSlotTypes(stationType: StationType): string[] {
  const availableTypes: string[] = [];
  if (stationType.payLinks.hour && stationType.payLinks.hour.trim() !== '') {
    availableTypes.push('hour');
  }
  if (stationType.payLinks.day && stationType.payLinks.day.trim() !== '') {
    availableTypes.push('day');
  }
  if (stationType.payLinks.month && stationType.payLinks.month.trim() !== '') {
    availableTypes.push('month');
  }
  return availableTypes;
}

// Function to get payment link for a specific slot type
function getPaymentLink(stationType: StationType, slotType: string): string {
  switch (slotType) {
    case 'hour':
      return stationType.payLinks.hour || '';
    case 'day':
      return stationType.payLinks.day || '';
    case 'month':
      return stationType.payLinks.month || '';
    default:
      return '';
  }
}

function slotTypeToHebrew(slotType: string): string {
  const map: Record<string, string> = {
    hour: 'שעתי',
    day: 'יומי',
    month: 'חודשי',
  };
  return map[slotType] || slotType;
}

const MIN_MONTH_COUNT = 1;
const MAX_MONTH_COUNT = 11;

function addMonths(date: Date, months: number): Date {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
}

function parseLocalDate(dateString: string): Date {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function dateToLocalString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function clampMonthCount(n: number): number {
  return Math.min(MAX_MONTH_COUNT, Math.max(MIN_MONTH_COUNT, n));
}

// Helper functions for default values
async function getDefaultDateTime(): Promise<{ date: string; startTime: string; endTime: string }> {
  const now = new Date();

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Use the later of today or MIN_ALLOWED_DATE as the starting point
  const startDate = today > MIN_ALLOWED_DATE ? today : MIN_ALLOWED_DATE;

  // Start with the minimum date (or tomorrow if minimum date is today)
  let defaultDate = new Date(startDate);
  if (startDate.getTime() === today.getTime()) {
    // If minimum date is today, start with tomorrow
    defaultDate.setDate(today.getDate() + 1);
  }

  // Skip weekends (Friday = 5, Saturday = 6)
  while (defaultDate.getDay() === 5 || defaultDate.getDay() === 6) {
    defaultDate.setDate(defaultDate.getDate() + 1);
  }

  // Format date
  const year = defaultDate.getFullYear();
  const month = String(defaultDate.getMonth() + 1).padStart(2, '0');
  const day = String(defaultDate.getDate()).padStart(2, '0');
  const dateString = `${year}-${month}-${day}`;

  // Get business hours from config
  const config = await getConfig();
  // Parse business hours from config (format: "HH:MM")
  const businessStartHour = config?.booking?.startHour
    ? parseInt(config.booking.startHour.split(':')[0])
    : 8;
  const businessEndHour = config?.booking?.endHour
    ? parseInt(config.booking.endHour.split(':')[0])
    : 19;

  // Use the exact start minute from config, or default to :30 if parsing fails
  const businessStartMinute = config?.booking?.startHour
    ? parseInt(config.booking.startHour.split(':')[1]) || 0
    : 30;

  // Calculate default start time (next hour, but for the default date)
  let startHour = now.getHours() + 1;

  // Ensure it's within business hours
  if (startHour < businessStartHour) {
    startHour = businessStartHour;
  } else if (startHour >= businessEndHour) {
    startHour = businessStartHour; // Start fresh at business start
  }

  const startTime = `${String(startHour).padStart(2, '0')}:${String(businessStartMinute).padStart(2, '0')}`;
  const endTime = `${String(startHour + 1).padStart(2, '0')}:${String(businessStartMinute).padStart(2, '0')}`;

  return { date: dateString, startTime, endTime };
}



const Booking: React.FC = () => {
  const [stationTypes, setStationTypes] = useState<StationType[]>([]);
  const [selectedType, setSelectedType] = useState<StationType | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedEndDate, setSelectedEndDate] = useState<string>('');
  const [selectedStartTime, setSelectedStartTime] = useState<string>('');
  const [selectedEndTime, setSelectedEndTime] = useState<string>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedStation, setSelectedStation] = useState<Station | null>(null);
  const [payLoading, setPayLoading] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);
  const [paySuccess, setPaySuccess] = useState<string | null>(null);
  const [availableStations, setAvailableStations] = useState<Station[]>([]);
  const [allStations, setAllStations] = useState<Station[]>([]);
  const [unavailableStationIds, setUnavailableStationIds] = useState<Set<string>>(new Set());
  const [hasSelectedDates, setHasSelectedDates] = useState<boolean>(true);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [bookingsVersion, setBookingsVersion] = useState(0);
  const [selectedSlotType, setSelectedSlotType] = useState<string>('');
  const [monthCount, setMonthCount] = useState(MIN_MONTH_COUNT);
  const [isAdmin, setIsAdmin] = useState(false);
  const [dateMode, setDateMode] = useState<'single' | 'recurring'>('single');
  const [seriesDates, setSeriesDates] = useState<string[]>([]);
  /** Per station id: series dates that conflict (recurring mode only) */
  const [stationConflictDates, setStationConflictDates] = useState<Record<string, string[]>>({});

  const auth = getAuth();
  const user = auth.currentUser;

  useEffect(() => {
    const run = async () => {
      if (user?.email) {
        setIsAdmin(await isUserAdmin(user.email));
      } else {
        setIsAdmin(false);
      }
    };
    run();
  }, [user?.email]);

  // Track step entries for funnel analysis
  useEffect(() => {
    const stepNames = ['choose_station_type', 'choose_datetime', 'choose_station'];
    if (activeStep >= 0 && activeStep < stepNames.length) {
      trackEvent('booking_step_entered', {
        user_email: user?.email,
        step: activeStep,
        step_name: stepNames[activeStep],
        station_type_id: selectedType?.id,
        station_type_name: selectedType?.typeFriendlyName,
        session_id: Date.now() + '_' + Math.random(), // Simple session tracking
      });
    }
  }, [activeStep, selectedType, user?.email]);

  // Track when users abandon the booking process
  useEffect(() => {
    return () => {
      // Track abandonment if user didn't complete booking
      if (activeStep < 2 || (activeStep === 2 && !paySuccess)) {
        trackEvent('booking_abandoned', {
          user_email: user?.email,
          abandoned_at_step: activeStep,
          step_name: ['choose_station_type', 'choose_datetime', 'choose_station'][activeStep],
          station_type_id: selectedType?.id,
          station_type_name: selectedType?.typeFriendlyName,
          had_selected_station_type: !!selectedType,
          had_selected_dates: hasSelectedDates,
          selected_slot_type: selectedSlotType,
        });
      }
    };
  }, [activeStep, selectedType, user?.email, hasSelectedDates, selectedSlotType, paySuccess]);

  // Initialize default values
  useEffect(() => {
    const initializeDefaults = async () => {
      const defaultDateTime = await getDefaultDateTime();
      setSelectedDate(defaultDateTime.date);
      setSelectedEndDate(defaultDateTime.date);
      setSelectedStartTime(defaultDateTime.startTime);
      setSelectedEndTime(defaultDateTime.endTime);
    };
    initializeDefaults();
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeToBookings(() => {
      setBookingsVersion(v => v + 1);
    });
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // Reset slot type and dates when entering step 1 (time picker)
  useEffect(() => {
    if (activeStep === 1 && selectedType && !selectedSlotType) {
      const availableSlotTypes = getAvailableSlotTypes(selectedType);
      const defaultSlotType = availableSlotTypes[0];
      setSelectedSlotType(defaultSlotType);
      setMonthCount(MIN_MONTH_COUNT);
      resetDateFields(defaultSlotType, MIN_MONTH_COUNT);
    }
  }, [activeStep, selectedType, selectedSlotType]);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!selectedType || activeStep < 1 || !selectedSlotType) {
        setAvailableStations([]);
        setAllStations([]);
        setUnavailableStationIds(new Set());
        setStationConflictDates({});
        setHasSelectedDates(false);
        return;
      }

      const isRecurring = dateMode === 'recurring';
      const needsHour = selectedSlotType === 'hour';
      const needsRange = selectedSlotType === 'day' || selectedSlotType === 'month';

      const hasHourInputs = selectedDate && selectedStartTime && selectedEndTime;
      const hasRangeInputs = selectedDate && selectedEndDate;
      const hasRecurringInputs =
        seriesDates.length > 0 &&
        (!needsHour || (selectedStartTime && selectedEndTime));

      // Always show all stations for the selected type
      const [stationsData, typesData] = await Promise.all([
        getAllStations(),
        getAllStationTypes(),
      ]);
      const filteredStations = stationsData.filter(s => String(s.typeId) === String(selectedType.id));

      const missingInputs = isRecurring
        ? !hasRecurringInputs
        : (needsHour && !hasHourInputs) || (needsRange && !hasRangeInputs);

      // If dates/times are not fully selected, show all stations but don't mark as unavailable
      if (missingInputs) {
        if (!cancelled) {
          setStationTypes(typesData);
          setAllStations(filteredStations);
          setAvailableStations([]);
          setUnavailableStationIds(new Set()); // Empty set - no stations marked as unavailable
          setStationConflictDates({});
          setHasSelectedDates(false); // User hasn't selected dates yet
          setAvailabilityLoading(false);
        }
        return;
      }

      setAvailabilityLoading(true);

      if (isRecurring) {
        const [bookings, ranges] = await Promise.all([
          getAllBookings(),
          buildSeriesRanges(
            seriesDates,
            selectedSlotType,
            selectedStartTime,
            selectedEndTime
          ),
        ]);

        const conflictsByStation: Record<string, string[]> = {};
        const available: Station[] = [];
        const unavailable = new Set<string>();

        for (const station of filteredStations) {
          const id = String(station.id);
          const conflicts = getConflictDates(bookings, id, seriesDates, ranges);
          conflictsByStation[id] = conflicts;

          if (conflicts.length === seriesDates.length) {
            unavailable.add(id);
            continue;
          }
          // Fully free or partial — still bookable
          available.push(station);
        }

        if (!cancelled) {
          setStationTypes(typesData);
          setAllStations(filteredStations);
          setAvailableStations(available);
          setUnavailableStationIds(unavailable);
          setStationConflictDates(conflictsByStation);
          setHasSelectedDates(true);
          setAvailabilityLoading(false);
        }
        return;
      }

      const { fromDate, toDate } = await createBookingDates(
        selectedSlotType,
        selectedDate,
        selectedEndDate || selectedDate,
        selectedStartTime,
        selectedEndTime
      );

      const availabilityChecks = filteredStations.map(async (station) => {
        const booked = await isStationBooked(String(station.id), fromDate, toDate);
        return { station, isAvailable: !booked };
      });
      const stationAvailability = await Promise.all(availabilityChecks);

      const available = stationAvailability.filter(s => s.isAvailable).map(s => s.station);
      const unavailable = new Set(stationAvailability.filter(s => !s.isAvailable).map(s => String(s.station.id)));

      if (!cancelled) {
        setStationTypes(typesData);
        setAllStations(filteredStations);
        setAvailableStations(available);
        setUnavailableStationIds(unavailable);
        setStationConflictDates({});
        setHasSelectedDates(true); // User has selected dates
        setAvailabilityLoading(false);
      }
    }
    run();
    return () => { cancelled = true; };
  }, [activeStep, selectedType, selectedSlotType, selectedDate, selectedEndDate, selectedStartTime, selectedEndTime, bookingsVersion, dateMode, seriesDates]);

  const steps = ['בחירת סוג חדר', 'בחירת תאריך וזמן', 'בחירת חדר'];

  // Custom stepper component for RTL support
  const CustomStepper = () => {
    const handleStepClick = (stepIndex: number) => {
      // Only allow navigation to previous steps or current step
      // For next steps, check if previous steps are completed
      if (stepIndex <= activeStep) {
        setActiveStep(stepIndex);
      } else if (stepIndex === activeStep + 1 && canProceedFromStep(activeStep)) {
        setActiveStep(stepIndex);
      }
    };

    return (
      <Box sx={{ width: '100%', mb: 4 }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          direction: 'rtl',
          flexWrap: { xs: 'wrap', md: 'nowrap' }, // Wrap on mobile
          gap: { xs: 1, sm: 2 }, // Responsive gap
        }}>
          {steps.map((step, index) => {
            const isClickable = index <= activeStep || (index === activeStep + 1 && canProceedFromStep(activeStep));

            return (
              <Box key={index} sx={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: { xs: 'column', md: 'row' }, // Stack vertically on mobile
                minWidth: { xs: '100px', sm: '120px', md: 'auto' },
              }}>
                {/* Step Circle */}
                <Box
                  onClick={() => handleStepClick(index)}
                  sx={{
                    width: { xs: 35, sm: 40 }, // Smaller on mobile
                    height: { xs: 35, sm: 40 },
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: index <= activeStep ? 'primary.main' : 'grey.300',
                    color: index <= activeStep ? 'white' : 'grey.600',
                    fontWeight: 'bold',
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    cursor: isClickable ? 'pointer' : 'default',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': isClickable ? {
                      transform: 'scale(1.1)',
                      boxShadow: 2,
                    } : {},
                    mb: { xs: 1, md: 0 }, // Margin bottom on mobile
                  }}
                >
                  {index + 1}
                </Box>

                {/* Step Label */}
                <Box sx={{
                  ml: { xs: 0, md: 1 },
                  mr: { xs: 0, md: 1 },
                  textAlign: 'center',
                  minWidth: { xs: '80px', sm: '100px', md: '120px' },
                  px: { xs: 1, sm: 2 }, // Horizontal padding
                }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: index === activeStep ? 'bold' : 'normal',
                      color: index <= activeStep ? 'primary.main' : 'text.secondary',
                      cursor: isClickable ? 'pointer' : 'default',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' }, // Smaller text on mobile
                      lineHeight: 1.2,
                    }}
                    onClick={() => handleStepClick(index)}
                  >
                    {step}
                  </Typography>
                </Box>

                {/* Connector Line - Hide on mobile or show horizontal */}
                {index < steps.length - 1 && (
                  <Box
                    sx={{
                      width: { xs: '2px', md: '60px' }, // Vertical line on mobile, horizontal on desktop
                      height: { xs: '20px', md: '2px' },
                      backgroundColor: index < activeStep ? 'primary.main' : 'grey.300',
                      mx: { xs: 0, md: 1 },
                      my: { xs: 1, md: 0 },
                      display: { xs: 'none', sm: 'block' }, // Hide connectors on very small screens
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  const handleNext = () => {
    // Validate before proceeding
    if (activeStep == 1 && !canProceedFromStep(activeStep)) {
      return;
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const resetDateFields = async (slotType?: string, months: number = MIN_MONTH_COUNT) => {
    const defaultDateTime = await getDefaultDateTime();
    setSelectedDate(defaultDateTime.date);
    if (slotType === 'month') {
      const endDate = addMonths(parseLocalDate(defaultDateTime.date), months);
      setSelectedEndDate(dateToLocalString(endDate));
    } else {
      setSelectedEndDate(defaultDateTime.date);
    }
    setSelectedStartTime(defaultDateTime.startTime);
    setSelectedEndTime(defaultDateTime.endTime);
  };

  const handleMonthCountChange = (n: number) => {
    const count = clampMonthCount(n);
    setMonthCount(count);
    if (!selectedDate || selectedSlotType !== 'month') return;
    const endDate = addMonths(parseLocalDate(selectedDate), count);
    setSelectedEndDate(dateToLocalString(endDate));
  };

  const handleBack = () => {
    const newStep = activeStep - 1;
    setActiveStep(newStep);

    // If going back to step 1 (time picker), reset slot type and dates
    if (newStep === 1 && selectedType) {
      const availableSlotTypes = getAvailableSlotTypes(selectedType);
      const defaultSlotType = availableSlotTypes[0];
      setSelectedSlotType(defaultSlotType);
      setMonthCount(MIN_MONTH_COUNT);
      resetDateFields(defaultSlotType, MIN_MONTH_COUNT);
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setSelectedType(null);
    setSelectedDate('');
    setSelectedEndDate('');
    setSelectedStartTime('');
    setSelectedEndTime('');
    setSelectedSlotType('');
    setMonthCount(MIN_MONTH_COUNT);
    setDateMode('single');
    setSeriesDates([]);
    setStationConflictDates({});
  };

  const handleDateModeChange = (mode: 'single' | 'recurring') => {
    setDateMode(mode);
    if (mode === 'single') {
      setSeriesDates([]);
      setStationConflictDates({});
    }
  };

  const handleSeriesDatesChange = (dates: string[]) => {
    setSeriesDates((prev) => (prev.join(',') === dates.join(',') ? prev : dates));
  };


  const canProceedFromStep = (step: number) => {
    switch (step) {
      case 0:
        return selectedType !== null;
      case 1:
        if (!selectedSlotType) return false;
        const needsHour = selectedSlotType === 'hour';
        const needsRange = selectedSlotType === 'day' || selectedSlotType === 'month';
        const isRecurring = dateMode === 'recurring';

        if (isRecurring) {
          if (seriesDates.length === 0) return false;
          if (needsHour && !(selectedStartTime && selectedEndTime)) return false;
        } else {
          const hasHourInputs = selectedDate && selectedStartTime && selectedEndTime;
          const hasRangeInputs = selectedDate && selectedEndDate;
          const hasBasicInputs = (needsHour && hasHourInputs) || (needsRange && hasRangeInputs);
          if (!hasBasicInputs) return false;
        }

        // For hourly bookings, validate time range
        if (needsHour && selectedType) {
          try {
            const startTime = new Date(`2000-01-01T${selectedStartTime}`);
            const endTime = new Date(`2000-01-01T${selectedEndTime}`);
            const durationHours = (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60);

            // Check minimum 1 hour
            if (durationHours < 1) return false;

            // Check baseHours constraints if applicable
            if (selectedType.baseHours) {
              const baseHours = selectedType.baseHours;

              if (selectedType.extraHourPrice) {
                // Tiered pricing: baseHours + any number of 1-hour increments
                if (durationHours >= baseHours) {
                  const extraHours = durationHours - baseHours;
                  // Extra hours must be whole numbers (1-hour increments)
                  const isValidTieredDuration = Math.abs(extraHours % 1) < 0.001;
                  if (!isValidTieredDuration) return false;
                } else {
                  // Less than base hours is invalid
                  return false;
                }
              } else {
                // Slot-based pricing: must be exact multiples of baseHours
                const isValidSlotDuration = Math.abs(durationHours % baseHours) < 0.001 && durationHours >= Math.max(1, baseHours);
                if (!isValidSlotDuration) return false;
              }
            }
          } catch (error) {
            // Invalid time format
            return false;
          }
        }

        // Friday/Saturday always blocked; Sunday meeting rooms only from 14:00
        const datesToValidate = isRecurring ? seriesDates : (selectedDate ? [selectedDate] : []);
        for (const dateStr of datesToValidate) {
          const [year, month, day] = dateStr.split('-').map(Number);
          const selectedDateObj = new Date(year, month - 1, day);
          selectedDateObj.setHours(0, 0, 0, 0);
          const dayOfWeek = selectedDateObj.getDay();

          if (dayOfWeek === 5 || dayOfWeek === 6) return false;

          if (selectedType?.type === 'meetingRoom' && dayOfWeek === 0) {
            // Day/month covers the morning — block Sundays entirely for those
            if (needsRange) return false;
            if (!selectedStartTime) return false;
            const [startHour] = selectedStartTime.split(':').map(Number);
            if (startHour < 14) return false;
          }
        }

        return true;
      case 2:
        return true; // Final step - user can book from here
      default:
        return false;
    }
  };

  const handleTypeSelected = (type: StationType) => {
    // Track station type selection
    trackEvent('booking_station_type_selected', {
      user_email: user?.email,
      station_type_id: type.id,
      station_type_name: type.typeFriendlyName,
      available_slot_types: getAvailableSlotTypes(type),
    });

    setSelectedType(type);
    const availableSlotTypes = getAvailableSlotTypes(type);
    const defaultSlotType = availableSlotTypes[0];
    setSelectedSlotType(defaultSlotType);
    setMonthCount(MIN_MONTH_COUNT);

    // Reset date fields when selecting a new station type
    resetDateFields(defaultSlotType, MIN_MONTH_COUNT);

    handleNext();
  };

  const handlePay = async (phoneNumber: string) => {
    if (!selectedStation || !selectedType || !selectedSlotType) return;

    // Track booking attempt
    trackEvent('booking_payment_attempted', {
      user_email: user?.email,
      station_id: selectedStation.id,
      station_name: selectedStation.id, // Station interface doesn't have name property
      station_type_id: selectedType.id,
      station_type_name: selectedType.typeFriendlyName,
      slot_type: selectedSlotType,
      selected_date: selectedDate,
      selected_end_date: selectedEndDate,
      selected_start_time: selectedStartTime,
      selected_end_time: selectedEndTime,
    });

    // Validate duration for hourly bookings with baseHours
    if (selectedSlotType === 'hour' && selectedType.pricePerBaseHour && selectedType.baseHours) {
      if (selectedStartTime && selectedEndTime) {
        const startTime = new Date(`2000-01-01T${selectedStartTime}`);
        const endTime = new Date(`2000-01-01T${selectedEndTime}`);
        const durationHours = (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60);

        const baseHours = selectedType.baseHours;
        let isValidDuration = false;
        let errorMessage = '';

        if (selectedType.extraHourPrice) {
          // Tiered pricing: baseHours + any number of 1-hour increments
          if (durationHours >= baseHours) {
            const extraHours = durationHours - baseHours;
            // Extra hours must be whole numbers (1-hour increments)
            isValidDuration = Math.abs(extraHours % 1) < 0.001;
            errorMessage = `הזמנה חייבת להיות מינימום ${baseHours} שעות, ואחר כך בקפיצות של שעה שלמה`;
          } else {
            errorMessage = `הזמנה חייבת להיות מינימום ${baseHours} שעות`;
          }
        } else {
          // Slot-based pricing: must be exact multiples of baseHours
          isValidDuration = Math.abs(durationHours % baseHours) < 0.001 && durationHours >= baseHours;
          errorMessage = `הזמנה חייבת להיות בחבילות של ${baseHours} שעות בלבד`;
        }

        if (!isValidDuration) {
          // Track validation error
          trackEvent('booking_validation_failed', {
            user_email: user?.email,
            station_id: selectedStation.id,
            error_type: 'invalid_duration',
            error_message: errorMessage,
            duration_hours: durationHours,
            base_hours: baseHours,
          });

          setPayError(errorMessage);
          setPayLoading(false);
          return;
        }
      }
    }

    setPayError(null);
    setPaySuccess(null);
    setPayLoading(true);

    const isSeries = dateMode === 'recurring' && seriesDates.length > 0;
    const conflictsForStation =
      isSeries && selectedStation
        ? stationConflictDates[String(selectedStation.id)] || []
        : [];
    const conflictSet = new Set(conflictsForStation);
    const datesToBook = isSeries
      ? seriesDates.filter((d) => !conflictSet.has(d))
      : [selectedDate];

    if (datesToBook.length === 0) {
      setPayError('אין מועדים פנויים להזמנה בעמדה זו');
      setPayLoading(false);
      return;
    }

    let failedDate = '';
    const createdIds: string[] = [];
    let groupId: string | undefined;

    try {
      for (const date of datesToBook) {
        failedDate = date;
        const endDate = isSeries ? date : (selectedEndDate || selectedDate);
        const { fromDate, toDate } = await createBookingDates(
          selectedSlotType,
          date,
          endDate,
          selectedStartTime,
          selectedEndTime
        );

        const result = await createBooking(
          String(selectedStation.id),
          fromDate,
          toDate,
          phoneNumber,
          groupId
        );
        createdIds.push(result.bookingId);
        groupId = result.groupId;

        trackEvent('booking_created_successfully', {
          user_email: user?.email,
          booking_id: result.bookingId,
          group_id: result.groupId,
          station_id: selectedStation.id,
          station_name: selectedStation.id,
          station_type_id: selectedType.id,
          station_type_name: selectedType.typeFriendlyName,
          slot_type: selectedSlotType,
          from_date: fromDate,
          to_date: toDate,
          is_series: isSeries,
          series_count: datesToBook.length,
          skipped_conflicts: conflictsForStation.length,
          has_payment_link: !!getPaymentLink(selectedType, selectedSlotType),
        });

        const paymentLink = getPaymentLink(selectedType, selectedSlotType);
        if (paymentLink && paymentLink.trim() !== '') {
          await logInfo('Payment redirect', {
            bookingId: result.bookingId,
            payLink: paymentLink,
            slotType: selectedSlotType,
            bookingData: result.booking,
          });
        }
      }

      failedDate = '';

      if (isSeries) {
        const skipNote =
          conflictsForStation.length > 0
            ? ` (${conflictsForStation.length} מועדים תפוסים דולגו)`
            : '';
        setPaySuccess(
          `${createdIds.length} הזמנות נרשמו בהצלחה${skipNote}! המזכירות תיצור איתך קשר ...`
        );
      } else {
        const paymentLink = getPaymentLink(selectedType, selectedSlotType);
        if (paymentLink && paymentLink.trim() !== '') {
          setPaySuccess('ההזמנה נרשמה בהצלחה! המזכירות תיצור איתך קשר ...');
          trackEvent('booking_payment_redirect', {
            user_email: user?.email,
            booking_id: createdIds[0],
            payment_link: paymentLink,
            slot_type: selectedSlotType,
          });
        } else {
          setPaySuccess('ההזמנה נרשמה בהצלחה!');
        }
      }

      setTimeout(() => {
        const dialogContent = document.querySelector('.MuiDialogContent-root');
        dialogContent?.scrollTo({ top: dialogContent.scrollHeight, behavior: 'smooth' });
      }, 0);
      setTimeout(() => {
        setDialogOpen(false);
        setPaySuccess(null);
      }, isSeries ? 2500 : 2000);
    } catch (e: any) {
      const translatedError = translateApiError(e);
      if (createdIds.length > 0) {
        setPayError(
          `נוצרו ${createdIds.length} מתוך ${datesToBook.length} הזמנות` +
            (failedDate ? ` (כשל ב־${failedDate})` : '') +
            `. ${translatedError}`
        );
      } else if (failedDate && isSeries) {
        setPayError(`כשל במועד ${failedDate}: ${translatedError}`);
      } else {
        setPayError(translatedError);
      }

      const errorCode = e.code || (e.message && e.message.includes('BOOKING_CONFLICT') ? 'BOOKING_CONFLICT' : null);
      if (errorCode === 'BOOKING_CONFLICT' || (e.message && (e.message.includes('already booked') || e.message.includes('עמדה זו כבר הוזמנה')))) {
        trackEvent('booking_conflict_detected', {
          user_email: user?.email,
          station_id: selectedStation.id,
          station_name: selectedStation.id,
          is_series: isSeries,
          created_count: createdIds.length,
          failed_date: failedDate,
        });
      } else {
        trackEvent('booking_creation_failed', {
          user_email: user?.email,
          station_id: selectedStation.id,
          station_name: selectedStation.id,
          error: e.message || 'Unknown error',
          errorCode: errorCode || 'UNKNOWN',
          is_series: isSeries,
          created_count: createdIds.length,
          failed_date: failedDate,
        });
      }
    }
    setPayLoading(false);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setPayError(null);
    setPaySuccess(null);
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return <ChooseStationType onTypeSelected={handleTypeSelected} />;
      case 1:
        if (!selectedType) return null;
        const availableSlotTypes = getAvailableSlotTypes(selectedType);
        const showSlotTypeSwitch = availableSlotTypes.length > 1;

        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="h5" component="h3" sx={{ mb: 3, textAlign: 'center' }}>
              בחירת תאריך וזמן • {selectedType.typeFriendlyName}
            </Typography>

            {showSlotTypeSwitch && (
              <Box sx={{ mb: 3, textAlign: 'center' }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  בחר סוג השכרה:
                </Typography>
                <ToggleButtonGroup
                  value={selectedSlotType}
                  exclusive
                  onChange={(_, newSlotType) => {
                    if (newSlotType) {
                      // Track slot type change
                      trackEvent('booking_slot_type_changed', {
                        user_email: user?.email,
                        previous_slot_type: selectedSlotType,
                        new_slot_type: newSlotType,
                        station_type_id: selectedType?.id,
                        station_type_name: selectedType?.typeFriendlyName,
                      });

                      setSelectedSlotType(newSlotType);

                      if (newSlotType === 'month') {
                        setDateMode('single');
                        setSeriesDates([]);
                        setStationConflictDates({});
                      }

                      if (newSlotType !== 'month') {
                        setMonthCount(MIN_MONTH_COUNT);
                      }

                      // For day/month types, auto-calculate end date if start date exists
                      if ((newSlotType === 'day' || newSlotType === 'month') && selectedDate) {
                        const startDate = parseLocalDate(selectedDate);
                        if (newSlotType === 'month') {
                          setMonthCount(MIN_MONTH_COUNT);
                          setSelectedEndDate(dateToLocalString(addMonths(startDate, MIN_MONTH_COUNT)));
                        } else {
                          setSelectedEndDate(dateToLocalString(startDate));
                        }
                      } else {
                        // Reset form fields when switching to hour type or when no date selected
                        setSelectedDate('');
                        setSelectedEndDate('');
                      }

                      // Always reset time fields when switching slot types
                      setSelectedStartTime('');
                      setSelectedEndTime('');
                    }
                  }}
                  aria-label="slot type selection"
                  sx={{
                    '& .MuiToggleButton-root': {
                      px: 4,
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 'bold',
                      border: '2px solid',
                      borderRadius: '25px',
                      margin: '0 8px',
                      '&.Mui-selected': {
                        backgroundColor: 'primary.main',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'primary.dark',
                        }
                      }
                    }
                  }}
                >
                  {availableSlotTypes.map((slotType) => (
                    <ToggleButton key={slotType} value={slotType}>
                      {slotTypeToHebrew(slotType)}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
            )}

            <DateTimePicker
              selectedType={selectedType}
              selectedSlotType={selectedSlotType}
              selectedDate={selectedDate}
              selectedEndDate={selectedEndDate}
              selectedStartTime={selectedStartTime}
              selectedEndTime={selectedEndTime}
              monthCount={monthCount}
              dateMode={dateMode}
              onDateChange={setSelectedDate}
              onEndDateChange={setSelectedEndDate}
              onStartTimeChange={setSelectedStartTime}
              onEndTimeChange={setSelectedEndTime}
              onMonthCountChange={handleMonthCountChange}
              onDateModeChange={handleDateModeChange}
              onSeriesDatesChange={handleSeriesDatesChange}
            />
          </Box>
        );
      case 2:
        // Track price display view
        if (selectedType) {
          trackEvent('booking_price_displayed', {
            user_email: user?.email,
            station_type_id: selectedType.id,
            station_type_name: selectedType.typeFriendlyName,
            price_per_hour: selectedType.pricePerBaseHour,
            price_per_day: selectedType.pricePerDay,
            price_per_month: selectedType.pricePerMonth,
            selected_slot_type: selectedSlotType,
          });
        }

        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="h5" component="h3" sx={{ mb: 3, textAlign: 'center' }}>
              בחירת חדר
            </Typography>

            {/* Price Display Section */}
            {selectedType && (
              <Box sx={{ mb: 4, p: 3, bgcolor: 'primary.light', borderRadius: 2, textAlign: 'center' }}>
                <Typography variant="h6" sx={{ mb: 2, color: 'primary.contrastText' }}>
                  מחירים - {selectedType.typeFriendlyName}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, flexWrap: 'wrap' }}>
                  {selectedType.pricePerBaseHour && (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.contrastText' }}>
                        {selectedType.pricePerBaseHour} ₪
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'primary.contrastText', opacity: 0.9 }}>
                        לחבילת בסיס שעתית
                      </Typography>
                    </Box>
                  )}
                  {selectedType.pricePerDay && (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.contrastText' }}>
                        {selectedType.pricePerDay} ₪
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'primary.contrastText', opacity: 0.9 }}>
                        ליום
                      </Typography>
                    </Box>
                  )}
                  {selectedType.pricePerMonth && (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.contrastText' }}>
                        {selectedType.pricePerMonth} ₪
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'primary.contrastText', opacity: 0.9 }}>
                        לחודש
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            <StationList
              allStations={allStations}
              availableStations={availableStations}
              unavailableStationIds={unavailableStationIds}
              hasSelectedDates={hasSelectedDates}
              stationTypes={stationTypes}
              availabilityLoading={availabilityLoading}
              stationConflictDates={dateMode === 'recurring' ? stationConflictDates : {}}
              seriesTotalCount={dateMode === 'recurring' ? seriesDates.length : 0}
              onStationClick={(station) => {
                // Track station selection
                trackEvent('booking_station_selected', {
                  user_email: user?.email,
                  station_id: station.id,
                  station_name: station.id, // Station interface doesn't have name property
                  station_type_id: selectedType?.id,
                  station_type_name: selectedType?.typeFriendlyName,
                });

                setSelectedStation(station);
                setDialogOpen(true);
              }}
            />
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <BackgroundWrapper>
      <Container maxWidth="lg" sx={{ py: 4, direction: 'rtl' }}>
        <Paper elevation={2} sx={{ p: 3, mb: 3, backgroundColor: 'rgba(255, 255, 255, 0.95)' }}>
          <Typography variant="h4" component="h1" sx={{ mb: 4, textAlign: 'center' }}>
            הזמנת חדרים ותחנות
          </Typography>

          <CustomStepper />

          <Box sx={{ minHeight: '400px' }}>
            {renderStepContent(activeStep)}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
            <Button
              color="inherit"
              disabled={activeStep === 0}
              onClick={handleBack}
              sx={{ mr: 1 }}
            >
              חזור
            </Button>
            <Box sx={{ flex: '1 1 auto' }} />
            {activeStep === steps.length - 1 ? (
              <Button onClick={handleReset}>
                התחל מחדש
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!canProceedFromStep(activeStep)}
              >
                הבא
              </Button>
            )}
          </Box>
        </Paper>

        <BookingDialog
          isOpen={dialogOpen}
          selectedStation={selectedStation}
          selectedType={selectedType}
          selectedSlotType={selectedSlotType}
          selectedDate={selectedDate}
          selectedEndDate={selectedEndDate}
          selectedStartTime={selectedStartTime}
          selectedEndTime={selectedEndTime}
          seriesDates={
            dateMode === 'recurring' && selectedStation
              ? seriesDates.filter(
                  (d) => !(stationConflictDates[String(selectedStation.id)] || []).includes(d)
                )
              : []
          }
          skippedConflictDates={
            dateMode === 'recurring' && selectedStation
              ? stationConflictDates[String(selectedStation.id)] || []
              : []
          }
          userEmail={user?.email || ''}
          userName={user?.displayName || ''}
          payLoading={payLoading}
          payError={payError}
          paySuccess={paySuccess}
          onClose={handleCloseDialog}
          onPay={handlePay}
          isAdmin={isAdmin}
        />
      </Container>
    </BackgroundWrapper>
  );
};

export default Booking;
